import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FEATURES } from '../../lib/features';

// Feature preview images mapping
const FEATURE_IMAGES: Record<string, string> = {
  tasks: '/tasks-preview.png',
  clients: '/clients-preview.png',
  orders: '/orders-preview.png',
  invoices: '/invoices-preview.png',
  calendar: '/calendar-preview.png',
  stats: '/stats-preview.png',
};

// Color schemes for each feature
const getFeatureColors = (key: string) => {
  const colorSchemes: Record<string, {
    border: string;
    bg: string;
    shadow: string;
    glow: string[];
    accents: string[];
  }> = {
    tasks: {
      border: 'border-blue-500/30',
      bg: 'bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-teal-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(59,130,246,0.4)]',
      glow: ['from-blue-500/20', 'via-cyan-500/20', 'to-teal-500/20'],
      accents: ['blue-400/50', 'cyan-400/50', 'teal-400/50', 'blue-400/50'],
    },
    clients: {
      border: 'border-emerald-500/30',
      bg: 'bg-gradient-to-br from-emerald-500/10 via-green-500/10 to-lime-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(16,185,129,0.4)]',
      glow: ['from-emerald-500/20', 'via-green-500/20', 'to-lime-500/20'],
      accents: ['emerald-400/50', 'green-400/50', 'lime-400/50', 'emerald-400/50'],
    },
    orders: {
      border: 'border-amber-500/30',
      bg: 'bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-yellow-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(245,158,11,0.4)]',
      glow: ['from-amber-500/20', 'via-orange-500/20', 'to-yellow-500/20'],
      accents: ['amber-400/50', 'orange-400/50', 'yellow-400/50', 'amber-400/50'],
    },
    invoices: {
      border: 'border-violet-500/30',
      bg: 'bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-fuchsia-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(139,92,246,0.4)]',
      glow: ['from-violet-500/20', 'via-purple-500/20', 'to-fuchsia-500/20'],
      accents: ['violet-400/50', 'purple-400/50', 'fuchsia-400/50', 'violet-400/50'],
    },
    calendar: {
      border: 'border-indigo-500/30',
      bg: 'bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(99,102,241,0.4)]',
      glow: ['from-indigo-500/20', 'via-purple-500/20', 'to-pink-500/20'],
      accents: ['indigo-400/50', 'purple-400/50', 'pink-400/50', 'indigo-400/50'],
    },
    stats: {
      border: 'border-rose-500/30',
      bg: 'bg-gradient-to-br from-rose-500/10 via-pink-500/10 to-red-500/10',
      shadow: 'shadow-[0_30px_120px_rgba(244,63,94,0.4)]',
      glow: ['from-rose-500/20', 'via-pink-500/20', 'to-red-500/20'],
      accents: ['rose-400/50', 'pink-400/50', 'red-400/50', 'rose-400/50'],
    },
  };
  
  return colorSchemes[key] || colorSchemes.tasks;
};

export const FeaturesTabs = () => {
  const [activeTab, setActiveTab] = useState('tasks');

  const activeFeature = FEATURES.find(f => f.key === activeTab) || FEATURES[0];

  return (
    <section id="features" className="py-16 md:py-24">
      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-white">
            Features Designed for Freelancers
          </h2>
          <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
            Everything you usually track across notes, chats, and spreadsheets — unified in one place.
          </p>
        </div>

        <div className="space-y-12">
          {/* Tab Buttons */}
          <div className="flex flex-wrap gap-3 justify-center">
            {FEATURES.map((feature) => (
              <motion.button
                key={feature.key}
                onClick={() => setActiveTab(feature.key)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === feature.key
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-[rgb(var(--fg))]'
                    : 'bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {feature.title}
              </motion.button>
            ))}
          </div>

          {/* Content */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <h3 className="text-2xl md:text-3xl font-semibold text-white">
                  {activeFeature.title}
                </h3>
                <p className="text-lg text-neutral-300">
                  {activeFeature.description}
                </p>
                <motion.a
                  href="/register"
                  className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Start prioritizing now →
                </motion.a>
              </motion.div>
            </AnimatePresence>

            {/* Right: Screenshot Placeholder or Image */}
            <motion.div
              key={activeFeature.key}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`rounded-xl border backdrop-blur-xl min-h-[320px] flex items-center justify-center p-8 overflow-hidden ${getFeatureColors(activeFeature.key).border} ${getFeatureColors(activeFeature.key).bg} ${getFeatureColors(activeFeature.key).shadow}`}
            >
              {FEATURE_IMAGES[activeFeature.key] ? (
                <motion.div 
                  className="relative w-full h-full flex items-center justify-center group"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Glow effect behind image */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${getFeatureColors(activeFeature.key).glow.join(' ')} rounded-2xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-300`} />
                  
                  {/* Image container with enhanced styling */}
                  <div className="relative z-10 w-full max-w-4xl">
                    <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)] border-2 border-white/20 group-hover:border-opacity-40 transition-all duration-300"
                      style={{ 
                        borderColor: `rgba(${activeFeature.key === 'tasks' ? '59,130,246' : activeFeature.key === 'clients' ? '16,185,129' : activeFeature.key === 'orders' ? '245,158,11' : activeFeature.key === 'invoices' ? '139,92,246' : activeFeature.key === 'calendar' ? '99,102,241' : '244,63,94'}, 0.2)`,
                      }}
                      onMouseEnter={(e) => {
                        const colors: Record<string, string> = {
                          tasks: 'rgba(59,130,246,0.4)',
                          clients: 'rgba(16,185,129,0.4)',
                          orders: 'rgba(245,158,11,0.4)',
                          invoices: 'rgba(139,92,246,0.4)',
                          calendar: 'rgba(99,102,241,0.4)',
                          stats: 'rgba(244,63,94,0.4)',
                        };
                        e.currentTarget.style.borderColor = colors[activeFeature.key] || colors.tasks;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                    >
                      {/* Shine effect overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none z-10" />
                      
                      {/* Image with quality enhancements */}
                      <img 
                        src={FEATURE_IMAGES[activeFeature.key]} 
                        alt={`${activeFeature.title} Preview`}
                        className="w-full h-auto rounded-2xl object-contain brightness-105 contrast-110 saturate-110 transition-transform duration-500 group-hover:scale-[1.01]"
                        style={{
                          imageRendering: 'crisp-edges',
                          WebkitImageRendering: 'crisp-edges',
                        }}
                        loading="eager"
                      />
                      
                      {/* Bottom gradient fade */}
                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent pointer-events-none rounded-b-2xl" />
                    </div>
                    
                    {/* Decorative corner accents */}
                    {(() => {
                      const accentColors: Record<string, string[]> = {
                        tasks: ['rgba(59,130,246,0.5)', 'rgba(6,182,212,0.5)', 'rgba(20,184,166,0.5)', 'rgba(59,130,246,0.5)'],
                        clients: ['rgba(16,185,129,0.5)', 'rgba(34,197,94,0.5)', 'rgba(132,204,22,0.5)', 'rgba(16,185,129,0.5)'],
                        orders: ['rgba(245,158,11,0.5)', 'rgba(249,115,22,0.5)', 'rgba(234,179,8,0.5)', 'rgba(245,158,11,0.5)'],
                        invoices: ['rgba(139,92,246,0.5)', 'rgba(168,85,247,0.5)', 'rgba(192,38,211,0.5)', 'rgba(139,92,246,0.5)'],
                        calendar: ['rgba(99,102,241,0.5)', 'rgba(168,85,247,0.5)', 'rgba(236,72,153,0.5)', 'rgba(99,102,241,0.5)'],
                        stats: ['rgba(244,63,94,0.5)', 'rgba(236,72,153,0.5)', 'rgba(239,68,68,0.5)', 'rgba(244,63,94,0.5)'],
                      };
                      const colors = accentColors[activeFeature.key] || accentColors.tasks;
                      return (
                        <>
                          <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 rounded-tl-lg opacity-60" style={{ borderColor: colors[0] }} />
                          <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 rounded-tr-lg opacity-60" style={{ borderColor: colors[1] }} />
                          <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 rounded-bl-lg opacity-60" style={{ borderColor: colors[2] }} />
                          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 rounded-br-lg opacity-60" style={{ borderColor: colors[3] }} />
                        </>
                      );
                    })()}
                  </div>
                </motion.div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-indigo-500/20 rounded-xl flex items-center justify-center">
                    <div className="w-8 h-8 bg-indigo-500/40 rounded" />
                  </div>
                  <p className="text-neutral-400 text-sm font-mono">{activeFeature.title} Preview</p>
                  <p className="text-xs text-neutral-500">Preview screenshot placeholder</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

